import React from 'react';

import { range } from 'lodash-es';

import Select from '@/components/ui/forms/Select';
import useInputChangeValue from '@/hooks/common/useInputChangeValue';

import styles from './TimeInputs.module.scss';

type Value = string | null;

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  innerRef?: React.Ref<HTMLInputElement>;
}

interface Props {
  minuteStep?: number;
  inputProps?: InputProps;
}

export const TIME_FORMAT = /^([0-1]\d|2[0-3]):[0-5]\d$/;

const TimeInputs: React.FC<Props> = ({
  minuteStep = 5,
  inputProps: { innerRef, ...inputProps } = { innerRef: undefined },
}) => {
  const inputRef = React.useRef<HTMLInputElement>();

  const { changeValue } = useInputChangeValue();

  const [hour, setHour] = React.useState<Value>(null);
  const [minute, setMinute] = React.useState<Value>(null);

  const [time, _setTime] = React.useState<Value>(null);
  const setTime = React.useCallback(
    (val: Value) => {
      _setTime(val);
      if (inputRef.current) {
        changeValue(inputRef.current, val);
      }
    },
    [changeValue],
  );

  const [hourIsDirty, setHourIsDirty] = React.useState<boolean>(false);
  const [minuteIsDirty, setMinuteIsDirty] = React.useState<boolean>(false);

  React.useEffect(() => {
    // 両inputがblurされたらblurする
    if (hourIsDirty && minuteIsDirty) {
      inputRef.current?.focus();
      inputRef.current?.blur();
    }
  }, [hourIsDirty, minuteIsDirty]);

  React.useEffect(() => {
    if (hourIsDirty && minuteIsDirty) {
      setTime(hour || minute ? `${hour || ''}:${minute || ''}` : null);
    }
  }, [hourIsDirty, minuteIsDirty, hour, minute, setTime]);

  // inputのvalueに値がある場合(componentの切り替えやback時)にTimeInputsの表示側にも値をsetする
  React.useEffect(() => {
    if (inputRef.current?.value && !(hourIsDirty && minuteIsDirty)) {
      const [hour, minute] = inputRef.current.value.split(':');
      setHour(hour);
      setMinute(minute);

      setHourIsDirty(true);
      setMinuteIsDirty(true);
    }
  }, [inputRef.current?.value, setTime, hourIsDirty, minuteIsDirty]);

  return (
    <div className={styles.wrapper}>
      <Select
        placeholder="例）12時"
        options={range(0, 24).map((i) => ({
          name: `${i}時`,
          value: `${i}`.padStart(2, '0'),
        }))}
        onChange={(val) => {
          setHour(val);
        }}
        onBlur={() => {
          setHourIsDirty(true);
        }}
        value={hour}
      />
      <Select
        placeholder="例）10分"
        options={range(0, 60, minuteStep).map((i) => ({
          name: `${i}分`,
          value: `${i}`.padStart(2, '0'),
        }))}
        onChange={(val) => {
          setMinute(val);
        }}
        onBlur={() => {
          setMinuteIsDirty(true);
        }}
        value={minute}
      />
      <input
        defaultValue={time || ''}
        {...inputProps}
        readOnly
        type="text"
        className={styles.hiddenInput}
        ref={(node) => {
          inputRef.current = node || undefined;
          if (typeof innerRef === 'function') {
            innerRef(node);
          } else if (innerRef) {
            (
              innerRef as React.MutableRefObject<HTMLInputElement | null>
            ).current = node;
          }
        }}
      />
    </div>
  );
};

export default TimeInputs;
